import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Box, VStack, Button, Text, Flex, Portal, Icon } from '@chakra-ui/react'
import { useLocation, useNavigate } from 'react-router-dom'
import { FiHome, FiFileText, FiClipboard, FiMessageCircle, FiBriefcase, FiUser, FiSettings, FiBarChart2, FiMail, FiHelpCircle, FiTrendingUp, FiStar, FiUsers, FiChevronDown, FiChevronRight } from 'react-icons/fi'
import { useClientView } from '../../contexts/ClientViewContext'
import { Tooltip } from '../ui/Tooltip'
import { PRACTITIONER_SPOTLIGHT_ENABLED } from '../../config/practitionerFeatures'

interface ProSidebarProps {
  isOpen: boolean
  onClose: () => void
  variant?: 'drawer' | 'sidebar'
  isCollapsed?: boolean
}

interface ProMenuItem {
  label: string
  href?: string
  icon: React.ComponentType
  tooltipLabel?: string
  tooltipDescription?: string
  children?: ProMenuItem[]
}

export const ProSidebar: React.FC<ProSidebarProps> = ({ isOpen, onClose, variant = 'sidebar', isCollapsed = false }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isDrawer = variant === 'drawer'
  const { isViewingClient, viewedClientProfile } = useClientView()

  // State for expanded sections
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    engagement: false
  })

  // Dynamic menu items - changes based on whether viewing a client
  const proItems: ProMenuItem[] = useMemo(() => [
    { 
      label: 'Pro Dashboard', 
      href: '/pro/clients', 
      icon: FiBriefcase,
      tooltipDescription: 'Manage your clients, view their profiles, and track their progress'
    },
    // When viewing client, show "Client Dashboard" that links to /dashboard
    // Otherwise show "Profile Dashboard" that links to redirect
    { 
      label: isViewingClient ? (viewedClientProfile?.name || 'Client') : 'Profile Dashboard', 
      href: isViewingClient ? '/dashboard' : '/redirect-to-profile', 
      icon: isViewingClient ? FiUser : FiHome,
      tooltipDescription: isViewingClient 
        ? 'View this client\'s health data and progress' 
        : 'Return to your personal profile dashboard'
    },
    {
      label: 'Grow Business',
      icon: FiTrendingUp,
      tooltipDescription: 'Tools to market your practice and acquire new clients',
      children: [
        // { 
        //   label: 'Ads Results', 
        //   href: '/pro/marketing', 
        //   icon: FiBarChart2,
        //   tooltipDescription: 'View your Google Ads campaign results and performance'
        // },
        ...(PRACTITIONER_SPOTLIGHT_ENABLED
          ? [{
              label: 'Spotlight',
              href: '/coach/spotlight-form',
              icon: FiStar,
              tooltipDescription: 'Get featured in our newsletter and Livewello Ads with a polished spotlight page showcasing your story, outcomes, and client feedback',
            }]
          : []),
        { 
          label: 'Report Card', 
          href: '/pro/report-card', 
          icon: FiFileText,
          tooltipDescription: 'Create and customize public report cards to showcase your practice metrics and client outcomes'
        },
        ...(PRACTITIONER_SPOTLIGHT_ENABLED
          ? [{
              label: 'Inquiries',
              href: '/pro/inquiries',
              icon: FiMessageCircle,
              tooltipDescription: 'Manage questions and booking requests from your Spotlight page',
            }]
          : []),
      ]
    },
    {
      label: 'Client Engagement',
      icon: FiUsers,
      tooltipDescription: 'Tools to engage with clients and track their feedback',
      children: [
        { 
          label: 'Analytics', 
          href: '/pro/analytics', 
          icon: FiBarChart2,
          tooltipDescription: 'View performance metrics, client success rates, and spotlight analytics'
        },
        { 
          label: 'Feedback', 
          href: '/pro/feedback', 
          icon: FiStar,
          tooltipDescription: 'Review and approve client feedback for your public profile'
        }
      ]
    },
    { 
      label: 'Templates', 
      tooltipLabel: 'Check-in Templates', 
      href: '/pro/templates', 
      icon: FiClipboard,
      tooltipDescription: 'Create and manage check-in templates for consistent client communication'
    },
    { 
      label: 'Settings', 
      href: '/pro/settings', 
      icon: FiSettings,
      tooltipDescription: 'Configure your Pro account settings, profile, and preferences'
    },
    { 
      label: 'Messages', 
      href: isViewingClient && viewedClientProfile?.profileId 
        ? `/messages?profile_id=${viewedClientProfile.profileId}` 
        : '/messages', 
      icon: FiMail,
      tooltipDescription: 'Communicate with clients through secure messaging'
    },
    { 
      label: 'Support', 
      href: '/pro/support', 
      icon: FiHelpCircle,
      tooltipDescription: 'Get help from our support team with Pro-specific questions'
    },
  ], [isViewingClient, viewedClientProfile?.name, viewedClientProfile?.profileId, PRACTITIONER_SPOTLIGHT_ENABLED])

  // Hover state for collapsed mode tooltips
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const [flyoutPosition, setFlyoutPosition] = useState<{ top: number; left: number } | null>(null)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Toggle section expansion
  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  // Auto-expand sections with active children
  useEffect(() => {
    proItems.forEach((item) => {
      if (item.children && item.children.length > 0) {
        const sectionKey = item.label.toLowerCase().replace(/\s+/g, '-')
        if (hasActiveChild(item) && !expandedSections[sectionKey]) {
          setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: true
          }))
        }
      }
    })
  }, [location.pathname, proItems])

  // Helper functions for hover with delay
  const handleMouseEnter = (href: string, target?: HTMLElement) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setHoveredItem(href)
    if (target) {
      const rect = target.getBoundingClientRect()
      setFlyoutPosition({ top: rect.top, left: rect.right + 2 })
    }
  }

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null)
      setFlyoutPosition(null)
    }, 100)
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  // Lock body scroll when drawer is open on mobile
  useEffect(() => {
    if (isDrawer && isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isDrawer, isOpen])

  // Normalize href to ignore query/hash when checking active state
  const normalizePath = (path?: string) => (path || '').split('?')[0].split('#')[0]

  const isActive = (href?: string) => {
    const normalizedHref = normalizePath(href)
    if (!normalizedHref) return false
    // When viewing client, /dashboard link should be active on client-related pages
    if (isViewingClient && normalizedHref === '/dashboard') {
      const clientPages = ['/dashboard', '/wellness-goals', '/genetic-reports', '/documents', '/health-records', '/chat']
      return clientPages.some(page => location.pathname === page || location.pathname.startsWith(page + '/'))
    }
    // Profile Dashboard link (when not viewing client) is never "active" - it's just an exit link
    if (normalizedHref === '/redirect-to-profile') {
      return false
    }
    // Pro Dashboard (Clients page) - active when on /pro/clients or /pro/dashboard (which redirects to clients)
    if (normalizedHref === '/pro/clients') {
      return location.pathname === '/pro/clients' || location.pathname === '/pro/dashboard'
    }
    // Unified messaging has both `/messages` and `/pro/messages` routes – treat both as active
    const messagingPaths = ['/messages', '/pro/messages']
    if (messagingPaths.includes(normalizedHref)) {
      return messagingPaths.some(
        (path) => location.pathname === path || location.pathname.startsWith(`${path}/`)
      )
    }
    // For other items, exact match or starts with the path
    return location.pathname === normalizedHref || location.pathname.startsWith(normalizedHref + '/')
  }

  // Check if any child item is active (for parent section highlighting)
  const hasActiveChild = (item: ProMenuItem): boolean => {
    if (!item.children) return false
    return item.children.some(child => child.href && isActive(child.href))
  }

  // Auto-expand sections with active children
  useEffect(() => {
    proItems.forEach((item) => {
      if (item.children && item.children.length > 0) {
        const sectionKey = item.label.toLowerCase().replace(/\s+/g, '-')
        const hasActive = item.children.some(child => child.href && isActive(child.href))
        if (hasActive && !expandedSections[sectionKey]) {
          setExpandedSections(prev => ({
            ...prev,
            [sectionKey]: true
          }))
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  const handleClick = (href: string) => {
    navigate(href)
    if (isDrawer) onClose()
  }

  const list = (
    <VStack align="stretch" gap={1} mt={8}>
      {proItems.map((item) => {
        // Handle items with children (collapsible sections)
        if (item.children && item.children.length > 0) {
          const sectionKey = item.label.toLowerCase().replace(/\s+/g, '-')
          const isExpanded = expandedSections[sectionKey] || false
          const hasActive = hasActiveChild(item)
          const ChevronIcon = isExpanded ? FiChevronDown : FiChevronRight

          return (
            <Box key={sectionKey} position="relative">
              {/* Parent section header */}
              <Button
                variant="ghost"
                justifyContent="space-between"
                w="full"
                h="50px"
                onClick={() => toggleSection(sectionKey)}
                position="relative"
                bg={hasActive ? 'brand.50' : undefined}
                color={hasActive ? 'brand.600' : 'gray.600'}
                _before={hasActive ? {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: '4px',
                  bg: 'brand.600',
                  borderRightRadius: '4px'
                } : undefined}
                _hover={{
                  bg: 'gray.100',
                  color: hasActive ? 'brand.600' : 'gray.900'
                }}
                borderRadius={0}
              >
                <Flex align="center" gap={3}>
                  <Icon as={item.icon} boxSize={5} />
                  {(!isCollapsed || isDrawer) && (
                    <Text fontSize="md" fontWeight="medium">
                      {item.label}
                    </Text>
                  )}
                </Flex>
                {(!isCollapsed || isDrawer) && (
                  <Icon as={ChevronIcon} boxSize={4} />
                )}
              </Button>

              {/* Sub-items */}
              {isExpanded && (!isCollapsed || isDrawer) && (
                <VStack align="stretch" gap={0} pl={8}>
                  {item.children.map((child) => {
                    const childActive = child.href ? isActive(child.href) : false
                    const button = (
                      <Button
                        key={child.href || child.label}
                        variant="ghost"
                        justifyContent="flex-start"
                        w="full"
                        h="45px"
                        onClick={() => child.href && handleClick(child.href)}
                        position="relative"
                        bg={childActive ? 'brand.50' : undefined}
                        color={childActive ? 'brand.600' : 'gray.600'}
                        _before={childActive ? {
                          content: '""',
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          bg: 'brand.600',
                          borderRightRadius: '4px'
                        } : undefined}
                        _hover={{
                          bg: 'gray.100',
                          color: childActive ? 'brand.600' : 'gray.900'
                        }}
                        borderRadius={0}
                      >
                        <Flex align="center" gap={3}>
                          <Icon as={child.icon} boxSize={4} />
                          <Text fontSize="sm" fontWeight="medium">
                            {child.label}
                          </Text>
                        </Flex>
                      </Button>
                    )
                    return child.tooltipDescription ? (
                      <Tooltip key={child.href || child.label} label={child.tooltipDescription} placement="right">
                        {button}
                      </Tooltip>
                    ) : button
                  })}
                </VStack>
              )}
            </Box>
          )
        }

        // Handle regular items without children
        const active = item.href ? isActive(item.href) : false
        const showFlyout = isCollapsed && !isDrawer && item.href && hoveredItem === item.href

        const button = (
          <Button
            variant="ghost"
            justifyContent={isCollapsed && !isDrawer ? 'center' : 'flex-start'}
            w="full"
            h="50px"
            onClick={() => item.href && handleClick(item.href)}
            position="relative"
            bg={active ? 'brand.50' : undefined}
            color={active ? 'brand.600' : 'gray.600'}
            _before={active ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '4px',
              bg: 'brand.600',
              borderRightRadius: '4px'
            } : undefined}
            _hover={{
              bg: 'gray.100',
              color: active ? 'brand.600' : 'gray.900'
            }}
            borderRadius={0}
            onMouseEnter={(e) => item.href && handleMouseEnter(item.href, e.currentTarget)}
            onMouseLeave={handleMouseLeave}
          >
            <Flex align="center" gap={3}>
              <Icon as={item.icon} boxSize={5} />
              {(!isCollapsed || isDrawer) && (
                <Text fontSize="md" fontWeight="medium">
                  {item.label}
                </Text>
              )}
            </Flex>
          </Button>
        )

        return (
          <Box key={item.href || item.label} position="relative">
            {item.tooltipDescription && (!isCollapsed || isDrawer) ? (
              <Tooltip label={item.tooltipDescription} placement="right">
                {button}
              </Tooltip>
            ) : button}

            {/* Collapsed state tooltip/flyout */}
            {showFlyout && flyoutPosition && (
              <Portal>
                <Box
                  position="fixed"
                  top={flyoutPosition.top}
                  left={flyoutPosition.left}
                  zIndex={9999}
                  bg="gray.800"
                  color="white"
                  px={3}
                  py={2}
                  borderRadius="md"
                  fontSize="sm"
                  boxShadow="md"
                  pointerEvents="none"
                  whiteSpace="nowrap"
                  maxW="250px"
                >
                  {item.tooltipDescription || item.tooltipLabel || item.label}
                </Box>
              </Portal>
            )}
          </Box>
        )
      })}
    </VStack>
  )

  if (isDrawer) {
    return (
      <>
        {/* Backdrop */}
        {isOpen && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            zIndex={1400}
            onClick={onClose}
          />
        )}
        {/* Drawer */}
        <Box
          position="fixed"
          top={0}
          left={isOpen ? 0 : '-100%'}
          bottom={0}
          w="280px"
          bg="bg"
          zIndex={1401}
          transition="left 0.3s ease"
          boxShadow="lg"
          pt="72px" // Account for navbar
        >
          {list}
        </Box>
      </>
    )
  }

  return (
    <Box
      position="fixed"
      left={0}
      top="72px" // Below navbar
      bottom={0}
      w={isCollapsed ? '64px' : '240px'}
      bg="bg"
      borderRight="1px"
      borderColor="gray.200"
      transition="width 0.3s ease"
      zIndex={100}
      overflowX="hidden"
      overflowY="auto"
    >
      {list}
    </Box>
  )
}

export default ProSidebar
